import { Action } from 'redux';
import { CheckoutActions, CheckoutActionTypes } from '../checkout.types';
import { DeliveryDetailsFormData } from 'models/Forms';
import { ACTION_CREATOR_NAME, errorToBasketErrorModel } from 'helpers';
import { put } from '@redux-saga/core/effects';
import { ErrorAction, AppState } from '../../root.types';
import { select } from 'redux-saga/effects';
import { api } from 'repositories/api';
import { CustexCustomerDetailsModel, AddressModel, HybrisCustomerDetailsModel, DeliveryTypes, CustexAddressTypes, CustexAddressModel, convertAddressToCustexAddress } from 'models';
import { BasketErrorModel } from 'models/Errors';
import { loadBasketSaga, setDeliveryTypeSaga } from 'store/basket/actions';
import { validateBasketReservationsSaga, validateCartSaga } from 'store/basket/actions/validation';
import { BasketActionTypes } from 'store/basket';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';
import { initializePaymentCalculatorSaga } from 'store/payment/actions';
import { loadDeliveryDetailsSaga } from './loadDeliveryDetails';

export interface UpdateDeliveryDetailsAction extends Action<CheckoutActionTypes.UPDATE_DELIVERY_DETAILS> {
    deliveryDetailsForm: DeliveryDetailsFormData;
}

export interface UpdateDeliveryDetailsActionStart extends Action<CheckoutActionTypes.UPDATE_DELIVERY_DETAILS_START> {
    deliveryDetailsForm: DeliveryDetailsFormData;
}

export interface UpdateDeliveryDetailsActionSuccess extends Action<CheckoutActionTypes.UPDATE_DELIVERY_DETAILS_SUCCESS> {
}

export interface UpdateDeliveryDetailsActionError extends ErrorAction<CheckoutActionTypes.UPDATE_DELIVERY_DETAILS_ERROR> {
    errors: BasketErrorModel[];
}

export const updateDeliveryDetails: (deliveryDetailsForm: DeliveryDetailsFormData) => UpdateDeliveryDetailsAction = (deliveryDetailsForm: DeliveryDetailsFormData) => ({
    type: CheckoutActionTypes.UPDATE_DELIVERY_DETAILS,
    deliveryDetailsForm
});
updateDeliveryDetails[ACTION_CREATOR_NAME] = 'updateDeliveryDetails';

export type UpdateDeliveryDetailsFunc = (...args: Parameters<typeof updateDeliveryDetails>) => void;

export function* updateDeliveryDetailsSaga({ deliveryDetailsForm }: UpdateDeliveryDetailsAction): BasketSagaReturnType {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.UPDATE_DELIVERY_DETAILS_START,
        deliveryDetailsForm
    });

    try {
        let state: AppState = yield select();
        let customerId = state.basketState.basket!.session.userId;
        let userName = state.basketState.basket!.user.name;

        let hybrisCustomerDetails: HybrisCustomerDetailsModel = {
            customerId,
            email: state.basketState.basket!.user.name,
            firstName: deliveryDetailsForm.firstName,
            surname: deliveryDetailsForm.surname,
            phone: deliveryDetailsForm.phone,
            title: deliveryDetailsForm.title,
            deliveryAddress: deliveryDetailsForm.deliveryAddress,
            billingAddress: deliveryDetailsForm.billingAddress,
            isCustomerInfoExists: true
        };

        let initialDeliveryCost = state.basketState.basket?.deliveryCost?.value;

        yield api.hybris.updateCustomerDeliveryDetails(userName, state.basketState.basket!.guid, hybrisCustomerDetails, hybrisCustomerDetails.deliveryAddress!);
        yield api.hybris.updateCustomerBillingDetails(userName, state.basketState.basket!.guid, hybrisCustomerDetails, hybrisCustomerDetails.billingAddress!);

        let custexCustomerDetails: CustexCustomerDetailsModel = yield api.custex.getCustomerDetails(customerId);

        setPhoneNumber(custexCustomerDetails, deliveryDetailsForm.phone);
        custexCustomerDetails.title = deliveryDetailsForm.title;
        custexCustomerDetails.firstName = deliveryDetailsForm.firstName;
        custexCustomerDetails.surname = deliveryDetailsForm.surname;
        setAddresses(custexCustomerDetails, deliveryDetailsForm.deliveryAddress!, deliveryDetailsForm.billingAddress!);

        yield api.custex.updateCustomerDetails(userName, customerId, custexCustomerDetails);

        yield callBasketSaga(loadBasketSaga);

        state = yield select();

        if (state.basketState.waitsForChoosingDeliveryType) {
            yield callBasketSaga(setDeliveryTypeSaga, {
                type: BasketActionTypes.SET_DELIVERY_TYPE,
                deliveryType: DeliveryTypes.NonMainlandDelivery
            });
            yield callBasketSaga(loadBasketSaga);
            state = yield select();
        }

        let isDeliveryCostChanged = initialDeliveryCost !== state.basketState.basket?.deliveryCost?.value;
        if (isDeliveryCostChanged) {
            yield callBasketSaga(initializePaymentCalculatorSaga);
        }

        yield callBasketSaga(loadDeliveryDetailsSaga);

        yield callBasketSaga(validateBasketReservationsSaga, {
            type: BasketActionTypes.VALIDATE_BASKET_RESERVATIONS,
            isBasketPage: false
        });

        yield callBasketSaga(validateCartSaga, {
            type: BasketActionTypes.VALIDATE_CART,
            isBasketPage: false
        });

        state = yield select();

        if (!state.basketState.validationResult.isValid || state.basketState.shouldShowReservationWarning) {
            // Delivery details updated successfully, but validation failed
            // Still dispatch SUCCESS to stop loading state
            yield put<CheckoutActions>({
                type: CheckoutActionTypes.UPDATE_DELIVERY_DETAILS_SUCCESS
            });

            return true;
        }

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.UPDATE_DELIVERY_DETAILS_SUCCESS
        });

        return true;
    } catch (errors) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.UPDATE_DELIVERY_DETAILS_ERROR,
            errors: Array.isArray(errors) ? errors : [errors],
            error: Array.isArray(errors)
                ? errorToBasketErrorModel(errors[0], 'updateDeliveryDetailsSaga')
                : errorToBasketErrorModel(errors, 'updateDeliveryDetailsSaga')
        });

        return false;
    }
}

function setPhoneNumber(custexCustomerDetails: CustexCustomerDetailsModel, newPhoneNumber: string): void {
    let phoneId: number | undefined = undefined;
    if (custexCustomerDetails.phoneNumbers.length > 0) {
        let phoneIds = custexCustomerDetails.phoneNumbers.map(p => p.id).filter(p => p);
        phoneId = phoneIds.length > 0 ? phoneIds[phoneIds.length - 1] : undefined;
    }

    custexCustomerDetails.phoneNumbers = [{
        id: phoneId,
        number: newPhoneNumber.replace(/[\s]/g, ''),
        primary: true
    }];
}

function setAddresses(custexCustomerDetails: CustexCustomerDetailsModel, deliveryAddress: AddressModel, billingAddress: AddressModel): void {
    // cloned from old code
    const deliveryAddressId = getAddressId(custexCustomerDetails.addresses.filter(a => a.addressTypeId === CustexAddressTypes.Delivery));
    const billingAddressId = getAddressId(custexCustomerDetails.addresses.filter(a => a.addressTypeId === CustexAddressTypes.Billing));

    custexCustomerDetails.addresses = [
        convertAddressToCustexAddress(deliveryAddress, deliveryAddressId, CustexAddressTypes.Delivery),
        convertAddressToCustexAddress(billingAddress, billingAddressId, CustexAddressTypes.Billing),
    ];
}

function getAddressId(addresses: CustexAddressModel[]): number | undefined {
    let addressId: number | undefined = undefined;

    if (addresses.length > 0) {
        let addressIds = addresses.map(a => a.id).filter(a => a);
        addressId = addressIds.length > 0 ? addressIds[addressIds.length - 1] : undefined;
    }

    return addressId;
}